<script setup lang="ts">
/**
 * Первый уровень двухуровневого каталога: плитка категорий.
 *
 * Колонки берём из настроек каталога — те же, что у карточек товара: две сетки
 * с разной плотностью на одной странице смотрятся случайностью, а не решением.
 */
import { computed } from 'vue';
import CategoryTile from './CategoryTile.vue';
import { categoryPath } from '@shared/lib';
import { categoryTileMinWidthPx, type AppearanceCatalogConfig } from '@shared/config';

const props = defineProps<{
  /** Категории каталога: [{ category, to, img, products }]. */
  categories: any[];
  config: AppearanceCatalogConfig;
}>();

defineEmits<{ (event: 'select', category: any): void }>();

/** Пустые категории в плитке не показываем — переход в них вёл бы в пустоту. */
const visibleCategories = computed(() =>
  (props.categories || []).filter((category) => Array.isArray(category?.products) && category.products.length > 0),
);

const gridStyle = computed(() => ({
  // Колонки — потолок, а не жёсткое число: на экранах поуже плитка сама укладывает
  // меньше колонок, как только карточка становится уже --pcard-min (см. .pcard-grid).
  '--pcard-cols-desktop': String(props.config.columnsDesktop),
  '--pcard-cols-mobile': String(props.config.columnsMobile),
  '--pcard-min': `${categoryTileMinWidthPx(props.config.columnsDesktop)}px`,
}));

/**
 * Адрес категории — её страница раздела меню.
 *
 * Раньше здесь был `/?c=<slug>`: адрес, по которому переход происходит внутри
 * главной. Для гостя это одно и то же (обычный клик по-прежнему остаётся на
 * главной), но роботу нужен адрес с собственным содержимым — параметр `c`
 * поисковик имеет полное право отбросить как незначащий.
 */
const categoryHref = (slug?: string) => categoryPath(slug);
</script>

<template>
  <section v-if="visibleCategories.length > 0" class="category-grid-section">
    <div class="pcard-grid" :style="gridStyle">
      <CategoryTile
        v-for="category in visibleCategories"
        :key="category.to || category.category"
        :category="category"
        :href="categoryHref(category.to)"
        :surface="config.categoryLabelSurface"
        :opacity="config.categoryLabelOpacity"
        :data-testid="`category-tile-${category.to || category.category}`"
        @select="$emit('select', category)"
      />
    </div>
  </section>
</template>

<style scoped lang="scss">
.category-grid-section {
  @apply pt-8 md:pt-12;

  :deep(.pcard-grid) {
    @apply mt-0;
  }
}
</style>
